import React, { FC } from 'react';
import { useQuery } from '@apollo/client';
import { Avatar, Image, Text, VStack } from '@chakra-ui/react';
import { Typography } from '@shinetools/sunshine-universal';

import folderImg from 'assets/brand/folder.png';
import useCompanyContext from 'common/hooks/useCompanyContext';
import Button from 'components/_core/Button';
import SunshineCard from 'components/_core/SunshineCard';
import Loader from 'components/Loader';

import { useBankTransferContext } from '../../../../../../contexts';
import { Recipient } from '../../types';
import { InternationalTransferRecipientsDocument } from './graphql/internationalTransferRecipients.gql';
import locales from './locales';

interface InternationalTransferRecipientListProps {
  onRecipientClick: (recipient: Recipient) => void;
  onRecipientCreateClick: () => void;
}

interface InternationalTransferRecipientListPlaceholderProps {
  onRecipientCreateClick: () => void;
}

const InternationalTransferRecipientListPlaceholder: FC<
  InternationalTransferRecipientListPlaceholderProps
> = ({ onRecipientCreateClick }) => {
  const {
    bankTransfer: { currency },
  } = useBankTransferContext('internationalTransferRecipient');

  return (
    <VStack gap="space-32" paddingTop="space-8">
      <Image height={90} src={folderImg} />
      <VStack gap="space-24">
        <Typography.Text size="tiny" variant="secondary">
          {locales.formatString(locales.noBeneficiaryForCurrency, {
            currency: currency.name.toLowerCase(),
          })}
        </Typography.Text>
        <Button
          icon="arrow-right"
          iconPosition="right"
          onClick={onRecipientCreateClick}
          variant="inline-primary"
        >
          {locales.createRecipient}
        </Button>
      </VStack>
    </VStack>
  );
};

export const InternationalTransferRecipientList: FC<
  InternationalTransferRecipientListProps
> = ({ onRecipientClick, onRecipientCreateClick }) => {
  const { companyProfileId, companyUserId } = useCompanyContext();
  if (!companyProfileId) {
    throw new Error(
      'companyProfileId is required in InternationalTransferRecipient',
    );
  }
  if (!companyUserId) {
    throw new Error(
      'companyUserId is required in InternationalTransferRecipient',
    );
  }

  const {
    bankTransfer: {
      currency,
      quote: { internationalTransferQuoteId },
    },
  } = useBankTransferContext('internationalTransferRecipient');
  const { data } = useQuery(InternationalTransferRecipientsDocument, {
    onError: () => {
      throw new Error('Something went wrong while fetching recipients');
    },
    variables: {
      input: {
        companyProfileId,
        companyUserId,
        currency: currency.code,
        internationalTransferQuoteId,
        lang: locales.getLanguage(),
      },
    },
  });

  if (!data) {
    return <Loader />;
  }

  if (data.internationalTransferRecipients.length === 0) {
    return (
      <InternationalTransferRecipientListPlaceholder
        onRecipientCreateClick={onRecipientCreateClick}
      />
    );
  }

  const selectableRecipients = data.internationalTransferRecipients.filter(
    (recipient) => recipient.isSelectable,
  );

  return (
    <SunshineCard.Group>
      {selectableRecipients.map((recipient) => (
        <SunshineCard
          key={recipient.internationalTransferRecipientId}
          onClick={() => onRecipientClick(recipient)}
        >
          <SunshineCard.Slot name="aside">
            <Avatar
              backgroundColor="grey.200"
              borderRadius="radius-12"
              color="grey.800"
              fontWeight="weight-500"
              name={recipient.fullName}
              size="md"
            />
          </SunshineCard.Slot>
          <VStack alignItems="flex-start" gap="space-2">
            <Typography.Text bold variant="primary">
              {recipient.fullName}
            </Typography.Text>
            <Text noOfLines={1} size="sm" variant="secondary">
              {recipient.currency} · {recipient.accountSummary}
            </Text>
          </VStack>
          <SunshineCard.Slot name="action">
            <SunshineCard.SunshineIcon name="chevron-right" />
          </SunshineCard.Slot>
        </SunshineCard>
      ))}
    </SunshineCard.Group>
  );
};
